import firebase from "firebase/compat";
import { listingStateLabel, listingTypeLabel } from "utils/listings/parser";
import type { Listing } from "../../@types/listings/listing";
import type { ProcessedListingData, PropertyType } from "../../@types/sharedSchema";
import type { ListingState, ListingType } from "../../@types/listings/unions";

/**
 * A market ad's `sellerName` + `sellerPhoneNumber` → the listing's seller entry.
 *
 * The scraped name is one free-text string ("משה כהן", or an agency name, or
 * occasionally something that is not a name at all). Splitting on the FIRST
 * space keeps multi-word surnames intact, which is the common Hebrew case; a
 * one-word name yields an empty surname rather than a guess.
 *
 * Returns null when there is nothing to seed, so the form renders its empty
 * seller section instead of a row of blanks.
 */
export function splitSellerIntoListingContacts(
  sellerName?: string,
  sellerPhoneNumber?: string
): Listing["listingContacts"] {
  const name = sellerName?.trim() ?? "";
  const phone = sellerPhoneNumber?.trim() ?? "";
  if (!name && !phone) return null;

  const spaceIndex = name.indexOf(" ");
  return [
    {
      callerFirstName: spaceIndex === -1 ? name : name.slice(0, spaceIndex),
      callerLastName: spaceIndex === -1 ? "" : name.slice(spaceIndex + 1).trim(),
      callerPhoneNumber: phone
    }
  ];
}

export function convertProcessedListingDataToListing(
  processedListingData: ProcessedListingData
): Listing {
  const {
    originalListingVisibility,
    address,
    addressCoordinates,
    roomsCount,
    buildingTotalFloors,
    price,
    squareMeter,
    description,
    propertyType,
    propertyCondition,
    entranceType,
    entranceDate,
    imageUrls,
    transactionType,
    addressDisplayMode,
    hasAirConditioning,
    hasBars,
    hasElevator,
    hasGarden,
    hasParking,
    hasStorage,
    hasTerrace,
    hasHousingUnit,
    hasProtectedSpace,
    hasMamak,
    hasMiklat,
    hasHandicappedAccess,
    sizeOfPlotM2,
    sizeOfGardenM2,
    sizeOfTerraceM2,
    parkingSpotsCount,
    listingStatus,
    locationDetails,
    purposeType,
    madlanBulletinId,
    madlanConfirmedAddress,
    madlanLocationResolution,
    madlanPublishedByWebotUserMail,
    sellerName,
    sellerPhoneNumber
  } = processedListingData;

  const { street, neighborhood, city, floor, houseNumber, apartmentNumber } = address;

  const parsedPictures = imageUrls?.map((src) => ({ id: src, src, alt: "" })) ?? null;

  const rawEvictionDate = entranceDate ? new Date(entranceDate) : null;

  const evictionDate = rawEvictionDate
    ? firebase.firestore.Timestamp.fromDate(rawEvictionDate)
    : null;

  type ParsedListing = Omit<
    Listing,
    | "id"
    | "name"
    | "owners"
    | "createdBy"
    | "ownersData"
    | "createdTime"
    | "updatedTime"
    | "createdByOrganization"
  >;

  const listing: ParsedListing = {
    city: city,
    address: street,
    neighborhood: neighborhood,
    listingType: propertyType ? reverseListingTypeLabel[propertyType] : null,
    listingState: propertyCondition ? reverseListingStateLabel[propertyCondition] : null,
    evictionType: entranceType,
    evictionDate: evictionDate,

    transactionType: transactionType ?? null,
    addressDisplayMode: addressDisplayMode ?? null,

    visibility: originalListingVisibility,
    floor: floor,
    rooms: roomsCount,

    price: price,
    floorOutOf: buildingTotalFloors ?? null,
    description: description ?? "",
    sizeOfPropertyM2: squareMeter ?? null,

    addressCoordinates: addressCoordinates ?? null,

    pictures: parsedPictures ?? null,

    hasBars: hasBars ?? false,
    hasGarden: hasGarden ?? false,
    hasParking: hasParking ?? false,
    hasStorage: hasStorage ?? false,
    hasTerrace: hasTerrace ?? false,
    hasElevator: hasElevator ?? false,
    housingUnit: hasHousingUnit ?? false,
    protectedSpace: hasProtectedSpace ?? false,
    // A market ad's ממ"ק / מקלט used to be dropped here, so a claimed Yad2 ad with a
    // מקלט became a managed listing without one.
    hasMamak: hasMamak ?? false,
    hasMiklat: hasMiklat ?? false,
    hasAirConditioning: hasAirConditioning ?? false,
    accessibleForDisabled: hasHandicappedAccess ?? false,

    sizeOfPlotM2: sizeOfPlotM2 ?? null,
    sizeOfGardenM2: sizeOfGardenM2 ?? null,
    sizeOfTerraceM2: sizeOfTerraceM2 ?? null,
    parkingSpotsCount: parkingSpotsCount ?? null,

    status: listingStatus ?? "onSale",
    locationDetails: locationDetails ?? "",

    purposeType: purposeType ?? null,

    // The remaining Listing fields that do NOT exist in ProcessedListingData
    fee: null,
    files: null,
    signDate: null,
    projectName: "",
    buyerOrigin: null,
    exclusivity: null,
    leaseEndDate: null,
    leaseStartDate: null,
    cleaningFee: null,
    priceHistory: null,
    streetNumber: houseNumber ?? null,
    userFavorites: [],
    additionalNotes: "",
    // The market ad's seller, seeded into the form's seller section.
    //
    // This used to be a hardcoded `null`, which is why claiming a listing lost
    // the owner's name and phone — the one thing the realtor most wants to keep
    // — and left her retyping them into a new contact card by hand.
    //
    // ⚠ Seeded, NOT saved as-is. Most yad2 seller numbers are VIRTUAL (073/055
    // masks) and this phone becomes a contact's document id, so the form must
    // show it, flag it, and let her correct it before anything is written.
    listingContacts: splitSellerIntoListingContacts(sellerName, sellerPhoneNumber),
    buyerOriginData: null,
    apartmentNumber: apartmentNumber ?? null,
    feeIncludesTaxes: false,
    buyerAttorneyName: "",
    municipalityTaxes: null,
    finalListingPrice: null,
    sellerAttorneyName: "",
    addressAreaNickname: "",
    pendingPriceUpdates: false,
    dealAdditionDetails: "",
    // NOTE the deliberate absence of `sourceListings`/`sourceListingIds`.
    //
    // They are in `NotEditableListing`, so the form's write path would drop them
    // anyway — and that is the point: the server owns those two fields and writes
    // them TOGETHER, which is the whole invariant behind the flat mirror. The
    // board's claim flow calls `POST /api/v1/listings/link-source` with the id
    // `onListingCreation` hands back, right after the form creates the copy.
    userDefinedListingId: "",
    buyerAttorneyPhoneNumber: "",
    sellerAttorneyPhoneNumber: "",
    madlanBulletinId: madlanBulletinId ?? null,
    madlanConfirmedAddress: madlanConfirmedAddress ?? null,
    madlanLocationResolution: madlanLocationResolution ?? null,
    madlanPublishedByWebotUserMail: madlanPublishedByWebotUserMail ?? null
  };

  return listing as Listing;
}

/**
 * Hebrew property-type label → internal `ListingType`. Exported because the
 * per-type form rules (does this property have an apartment number? a floor?)
 * live keyed by `ListingType`, while everything that comes back from
 * Algolia / Firestore carries the Hebrew `propertyType` label instead.
 */
export const reverseListingTypeLabel = Object.fromEntries(
  Object.entries(listingTypeLabel).map(([k, v]) => [v, k])
) as Record<PropertyType, ListingType>;

const reverseListingStateLabel = Object.fromEntries(
  Object.entries(listingStateLabel).map(([k, v]) => [v, k])
) as Record<string, ListingState>;
